import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { FaLinkedin, FaGithub } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { trackEvent } from "../utils/analytics";
import { useNavigate } from "react-router-dom";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/myzjedpe";
const WHATSAPP_API = "https://api.callmebot.com/whatsapp.php";
const WHATSAPP_PHONE = "+256753257102";
const WHATSAPP_API_KEY = "2192913";

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");
  const [isSending, setIsSending] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFocus = (field) => {
    trackEvent("contact_field_focus", {
      category: "Contact",
      label: field,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("");
    setIsSending(true);

    const { name, email, message } = formData;

    try {
      // ✅ Submit to Formspree
      const formRes = await axios.post(FORMSPREE_ENDPOINT, formData, {
        headers: { Accept: "application/json" },
      });

      if (formRes.status === 200 && formRes.data.ok) {
        // ✅ Send WhatsApp alert (non-blocking)
        const encodedMsg = encodeURIComponent(
          `📬 New Portfolio Contact\n\nName: ${name}\nEmail: ${email}\nMessage: ${message}`
        );

        axios
          .get(`${WHATSAPP_API}?phone=${WHATSAPP_PHONE}&apikey=${WHATSAPP_API_KEY}&text=${encodedMsg}`)
          .then(() => console.log("✅ WhatsApp alert sent"))
          .catch((wErr) => console.warn("⚠️ WhatsApp error:", wErr.message));

        setFormData({ name: "", email: "", message: "" });
        trackEvent("contact_submit", {
          event_category: "Contact",
          event_label: "Contact Form Submitted",
        });

        navigate("/thank-you");
      } else {
        throw new Error("Formspree rejected the request.");
      }
    } catch (err) {
      console.warn("❌ Contact error:", err.message);
      setStatus("❌ Something went wrong. Please try again.");
      trackEvent("contact_submit_failed", {
        event_category: "Contact",
        event_label: "Form Submission Error",
        error: err.message,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 dark:from-slate-900 dark:to-slate-800 p-8 flex items-center justify-center transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 transition-colors duration-300"
      >
        <h1 className="text-4xl font-bold text-center text-blue-700 dark:text-blue-400 mb-6">Get In Touch</h1>

        {status && (
          <div
            className={`p-4 mb-6 rounded text-center text-white ${
              status.includes("successfully") ? "bg-green-500" : "bg-red-500"
            }`}
          >
            {status}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-400 transition-colors duration-300"
            type="text"
            name="name"
            placeholder="Your Name"
            value={formData.name}
            onChange={handleChange}
            onFocus={() => handleFocus("name")}
            required
          />
          <input
            className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-400 transition-colors duration-300"
            type="email"
            name="email"
            placeholder="Your Email"
            value={formData.email}
            onChange={handleChange}
            onFocus={() => handleFocus("email")}
            required
          />
          <textarea
            className="w-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 p-3 rounded focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-400 transition-colors duration-300"
            name="message"
            placeholder="Your Message"
            value={formData.message}
            onChange={handleChange}
            onFocus={() => handleFocus("message")}
            rows="5"
            required
          />
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={isSending}
            className="w-full flex items-center justify-center bg-blue-600 dark:bg-blue-500 text-white py-3 rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition disabled:opacity-50"
          >
            {isSending && <ImSpinner2 className="animate-spin mr-2" />}
            {isSending ? "Sending..." : "Send Message"}
          </motion.button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-2">Or reach me directly:</p>
          <p className="font-semibold text-gray-900 dark:text-gray-100">
            📧{" "}
            <a href="mailto:munjijames02@gmail.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              munjijames02@gmail.com
            </a>
          </p>
          <p className="font-semibold text-gray-900 dark:text-gray-100">
            📱{" "}
            <a href="tel:+256753257102" className="text-blue-600 dark:text-blue-400 hover:underline">
              +256 753 257102
            </a>
          </p>
        </div>

        <div className="flex justify-center mt-6 space-x-6 text-blue-700 dark:text-blue-400 text-2xl">
          <a
            href="https://www.linkedin.com/in/munjwok-james-alala-4b5416327/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-900 dark:hover:text-blue-300 transition"
          >
            <FaLinkedin />
          </a>
          <a
            href="https://github.com/munji74"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-900 transition"
          >
            <FaGithub />
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
